<script setup>
// SiteUp (F2): chip "quem atende" — mostra o assistente de IA ativo na conversa e,
// ao clicar, permite trocar manualmente (fixa), fixar/soltar a troca automática, ou
// devolver para o assistente inicial da inbox (o Centralizador). Padrão visual do
// Chatwoot (DropdownBody + ícones lucide). Só aparece quando a conversa tem agente
// Captain (ai_agent_mode presente). Lê o assistente ativo do show (fresco) e
// atualiza na troca manual pelo retorno do endpoint.
import { computed, ref, watch, onMounted, useSlots } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ConversationAiAgentAPI from 'dashboard/api/conversationAiAgent';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import {
  resolveAiAgentMode,
  isAutonomousMode,
  hasActiveAiAgent,
} from 'dashboard/helper/aiAgentMode';

// SiteUp: compact = header estreito — esconde labels, mantém ícone+tooltip.
const props = defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();
const { t } = useI18n();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const assistants = useMapGetter('captainAssistants/getRecords');

const active = ref(null);
const pinned = ref(false);
const open = ref(false);
const saving = ref(false);
const toggleRef = ref(null);

const conversationId = computed(() => currentChat.value?.id);
const hasAgent = computed(() => hasActiveAiAgent(currentChat.value));
const activeName = computed(() => active.value?.name || 'IA');

// SiteUp (v350): status "IA ativa/pausada" + toggle aqui na BARRA (antes só dentro
// do menu ✨). Mesma lógica do AiAgentMenu: só no modo autônomo.
const settings = computed(
  () => currentChat.value?.additional_attributes?.ai_agent || {}
);
const currentMode = computed(() => resolveAiAgentMode(currentChat.value));
const paused = computed(() => settings.value.paused === true);
const isAutonomous = computed(() => isAutonomousMode(currentMode.value));
// SiteUp: modos de SUGESTAO (a IA gera rascunho, nao responde sozinha). O toggle
// de pausa vale aqui tambem — pausar por conversa desliga as sugestoes da IA nela.
const isSuggesting = computed(
  () =>
    currentMode.value === 'suggest_auto' ||
    currentMode.value === 'suggest_manual'
);
const canControlAi = computed(() => isAutonomous.value || isSuggesting.value);
// SiteUp: o estado da IA é independente do status do ticket. `paused` é a fonte
// de verdade para o botão da barra; status open/resolved/pending só organiza a fila.
const aiActive = computed(() => canControlAi.value && !paused.value);
const showStatus = computed(() => canControlAi.value);

// SiteUp: estado do follow-up (re-engajamento) desta conversa, lido do show (fresco)
// e atualizado pelo retorno do endpoint ao pausar/retomar. enabled = o assistente tem
// follow-up ligado; paused = flag skipped (o Captain::FollowUpJob já respeita).
const followupState = ref(null);
const followupEnabled = computed(() => followupState.value?.enabled === true);
const followupPaused = computed(() => followupState.value?.paused === true);
const followupCount = computed(() => followupState.value?.count || 0);
// O follow-up automático SÓ dispara no modo autônomo e quando a IA não está pausada
// (mesma regra do Captain::FollowUpJob#captain_blocked?). Então o badge só aparece
// JUNTO do indicador da IA (showStatus) — nunca sozinho num modo onde não funciona —
// e reflete o pause da IA: IA pausada => follow-up também não roda.
const showFollowup = computed(
  () => followupEnabled.value && showStatus.value && isAutonomous.value
);

// SiteUp V2 (G4): pílulas de estado separadas (.hpill) + Resolve branco ao lado.
const showActionCluster = computed(
  () => (hasAgent.value && showStatus.value) || Boolean(slots.trailing)
);

const syncFromChat = () => {
  active.value = currentChat.value?.ai_agent_active_assistant || null;
  pinned.value = currentChat.value?.ai_agent_pinned === true;
  followupState.value = currentChat.value?.ai_agent_followup || null;
};
watch(() => currentChat.value?.id, syncFromChat, { immediate: true });
watch(
  () => currentChat.value?.ai_agent_followup,
  value => {
    followupState.value = value || null;
  }
);
watch(
  () => currentChat.value?.ai_agent_active_assistant,
  value => {
    if (value) active.value = value;
  }
);
watch(
  () => currentChat.value?.ai_agent_pinned,
  value => {
    pinned.value = value === true;
  }
);

onMounted(() => {
  if (!assistants.value?.length) {
    store.dispatch('captainAssistants/get').catch(() => {});
  }
});

const applyAiAgentState = state => {
  if (!state || !currentChat.value) return;

  const additionalAttributes = {
    ...(currentChat.value.additional_attributes || {}),
    ai_agent: {
      ...(currentChat.value.additional_attributes?.ai_agent || {}),
      active: state.active,
      mode: state.mode,
      paused: state.paused,
      pinned: state.pinned,
    },
  };

  store.dispatch('updateConversation', {
    ...currentChat.value,
    status: state.conversation_status || currentChat.value.status,
    updated_at: state.conversation_updated_at || new Date().toISOString(),
    additional_attributes: additionalAttributes,
    ai_agent_mode: state.mode,
    ai_agent_active: state.active,
    ai_agent_paused: state.paused,
    ai_agent_pinned: state.pinned,
    ai_agent_active_assistant: state.active_assistant,
    ai_agent_followup: state.followup,
  });

  if (state.active_assistant) active.value = state.active_assistant;
  pinned.value = state.pinned === true;
  followupState.value = state.followup || null;
};

const persist = async data => {
  if (!conversationId.value || saving.value) return null;
  saving.value = true;
  try {
    const { data: state } = await ConversationAiAgentAPI.update(
      conversationId.value,
      data
    );
    applyAiAgentState(state);
    return state;
  } catch (error) {
    useAlert('Não foi possível atualizar o agente de IA.');
    return null;
  } finally {
    saving.value = false;
  }
};

const switchTo = async assistant => {
  if (assistant.id === active.value?.id) {
    open.value = false;
    return;
  }
  const state = await persist({ assistant_id: assistant.id });
  if (state) {
    active.value = state.active_assistant || {
      id: assistant.id,
      name: assistant.name,
    };
    pinned.value = state.pinned === true;
  }
  open.value = false;
};

const togglePin = async () => {
  const state = await persist({ pinned: !pinned.value });
  if (state) pinned.value = state.pinned === true;
};

const resetToInbox = async () => {
  const state = await persist({ assistant_id: 'reset' });
  if (state) {
    active.value = state.active_assistant || null;
    pinned.value = false;
  }
  open.value = false;
};

const pauseAi = async () => {
  await persist({ paused: true });
};

const returnToAi = async () => {
  // SiteUp: ativar a IA "solta" o lead pro repouso. O backend resolve a conversa
  // (release_ai_if_reactivated, na transição paused true->false) e ela reabre sozinha
  // por modo quando o lead voltar. O botão não depende do status atual do ticket.
  await persist({ paused: false });
};

// SiteUp: pausar/retomar o follow-up SÓ desta conversa (grava captain_followup.skipped
// via o mesmo endpoint). Independente do pause da resposta autônoma da IA.
const setFollowupPaused = async value => {
  const state = await persist({ followup_paused: value });
  if (state?.followup) followupState.value = state.followup;
};
const pauseFollowup = () => setFollowupPaused(true);
const resumeFollowup = () => setFollowupPaused(false);
</script>

<template>
  <div class="inline-flex items-center gap-1.5 sm:gap-2 min-w-0">
    <div v-if="hasAgent" class="relative inline-flex min-w-0">
      <button
        ref="toggleRef"
        type="button"
        class="inline-flex items-center gap-1.5 rounded-md px-1.5 sm:px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50 min-w-0"
        :disabled="saving"
        @click="open = !open"
      >
        <span class="i-ri-robot-line size-3.5 shrink-0 text-n-slate-10" />
        <span
          class="truncate"
          :class="props.compact ? 'max-w-[4.5rem]' : 'max-w-[10rem]'"
        >
          {{ activeName }}
        </span>
        <span
          v-if="pinned"
          v-tooltip.top="t('CONVERSATION.HEADER.AI_ASSISTANT_PINNED')"
          class="i-ri-pushpin-line size-3 shrink-0 text-n-amber-11"
        />
        <span class="i-ri-arrow-down-s-line size-3 shrink-0 text-n-slate-10" />
      </button>

      <DropdownBody
        v-if="open"
        v-on-click-outside="[() => (open = false), { ignore: [toggleRef] }]"
        class="min-w-60 top-full mt-2 ltr:right-0 rtl:left-0 z-50 [&>ul]:px-2 [&>ul]:py-2 [&>ul]:gap-0.5"
      >
        <div class="px-2 pb-1 text-xs font-medium text-n-slate-10">
          {{ t('CONVERSATION.HEADER.AI_SERVING_NOW') }}
        </div>
        <button
          v-for="assistant in assistants || []"
          :key="assistant.id"
          type="button"
          class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="saving"
          @click="switchTo(assistant)"
        >
          <span class="i-ri-robot-line size-3.5 shrink-0 text-n-slate-10" />
          <span class="flex-1 truncate text-left">{{ assistant.name }}</span>
          <span
            v-if="assistant.id === active?.id"
            class="i-ri-check-line size-3.5 shrink-0 text-n-teal-11"
          />
        </button>

        <div class="my-1.5 h-px w-full bg-n-strong" />

        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="saving"
          @click="togglePin"
        >
          <span
            :class="pinned ? 'i-ri-pushpin-line' : 'i-ri-pushpin-line'"
            class="size-3.5 shrink-0 text-n-slate-10"
          />
          {{
            pinned
              ? t('CONVERSATION.HEADER.AI_UNPIN_ASSISTANT')
              : t('CONVERSATION.HEADER.AI_PIN_ASSISTANT')
          }}
        </button>
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
          :disabled="saving"
          @click="resetToInbox"
        >
          <span
            class="i-ri-anticlockwise-line size-3.5 shrink-0 text-n-slate-10"
          />
          {{ t('CONVERSATION.HEADER.AI_RESET_ASSISTANT') }}
        </button>
      </DropdownBody>
    </div>

    <!-- SiteUp V2 (G4): pílulas separadas — .hpill "IA ativa" + Pausar + Resolver branco -->
    <div
      v-if="showActionCluster"
      class="inline-flex items-center gap-1.5 shrink-0"
    >
      <template v-if="hasAgent && showStatus">
        <span
          class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-n-alpha-2 text-[12.5px] font-bold leading-none text-n-slate-12 shrink-0"
          role="status"
          aria-live="polite"
          :aria-label="
            aiActive
              ? t('CONVERSATION.HEADER.AI_ACTIVE')
              : t('CONVERSATION.HEADER.AI_PAUSED')
          "
        >
          <span
            class="size-[7px] rounded-full shrink-0"
            :class="aiActive ? 'bg-n-teal-9 animate-pulse' : 'bg-n-amber-9'"
            aria-hidden="true"
          />
          <span v-if="!props.compact" class="whitespace-nowrap">
            {{
              aiActive
                ? t('CONVERSATION.HEADER.AI_ACTIVE')
                : t('CONVERSATION.HEADER.AI_PAUSED')
            }}
          </span>
        </span>
        <button
          v-tooltip.top="
            props.compact
              ? aiActive
                ? t('CONVERSATION.HEADER.PAUSE_AI')
                : t('CONVERSATION.HEADER.RESUME_AI')
              : null
          "
          type="button"
          class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-n-alpha-2 text-[12.5px] font-bold leading-none hover:bg-n-solid-2 disabled:opacity-50 shrink-0"
          :class="aiActive ? 'text-n-slate-12' : 'text-n-teal-11'"
          :disabled="saving"
          :aria-label="
            aiActive
              ? t('CONVERSATION.HEADER.PAUSE_AI')
              : t('CONVERSATION.HEADER.RESUME_AI')
          "
          @click="aiActive ? pauseAi() : returnToAi()"
        >
          <!-- SiteUp: teenyicons stroke (linhas finas). ph/lucide pause são fill grosso. -->
          <span
            :class="
              aiActive
                ? 'i-teenyicons-pause-outline'
                : 'i-ri-anticlockwise-line'
            "
            class="size-3.5 shrink-0"
            aria-hidden="true"
          />
          <span v-if="!props.compact" class="whitespace-nowrap">
            {{
              aiActive
                ? t('CONVERSATION.HEADER.PAUSE_AI')
                : t('CONVERSATION.HEADER.RESUME_AI')
            }}
          </span>
        </button>
      </template>
      <slot name="trailing" />
    </div>

    <button
      v-if="hasAgent && showFollowup && !paused"
      v-tooltip.top="
        followupPaused
          ? t('CONVERSATION.HEADER.FOLLOWUP_PAUSED_TIP')
          : followupCount
            ? t('CONVERSATION.HEADER.FOLLOWUP_ACTIVE_COUNT_TIP', {
                count: followupCount,
              })
            : t('CONVERSATION.HEADER.FOLLOWUP_ACTIVE_TIP')
      "
      type="button"
      class="inline-flex items-center justify-center rounded-md p-1 hover:bg-n-alpha-2 disabled:opacity-50 shrink-0"
      :disabled="saving"
      :aria-label="
        followupPaused
          ? t('CONVERSATION.HEADER.RESUME_FOLLOWUP')
          : t('CONVERSATION.HEADER.PAUSE_FOLLOWUP')
      "
      @click="followupPaused ? resumeFollowup() : pauseFollowup()"
    >
      <span
        class="size-3.5 shrink-0"
        :class="
          followupPaused
            ? 'i-ri-alarm-line text-n-amber-11'
            : 'i-ri-alarm-line text-n-teal-11'
        "
        aria-hidden="true"
      />
    </button>
  </div>
</template>
